import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as base64js from "base64-js";

const CompleteRegistration = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [registrationStep, setRegistrationStep] = useState('password'); // 'password' or 'device'
  
  // Bluetooth device states
  const [device, setDevice] = useState(null);
  const [deviceConnected, setDeviceConnected] = useState(false);
  const [macAddress, setMacAddress] = useState("");
  const [uniqueId, setUniqueId] = useState("");
  const [deviceInfo, setDeviceInfo] = useState({ name: "" });
  const [skipDevice, setSkipDevice] = useState(false);

  const notifySuccess = (message) => toast.success(message);
  const notifyError = (message) => toast.error(message);
  const notifyInfo = (message) => toast.info(message);

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await axios.get(
        `/api/auth/verify-registration-token/${token}`
      );
      setTokenValid(true);
      setUserInfo(res.data.user);
      notifyInfo("Please set your password to complete registration.");
    } catch (err) {
      notifyError("Invalid or expired registration link.");
      setTimeout(() => navigate("/"), 3000);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      notifyError("Password must be at least 6 characters long.");
      return;
    }
    
    if (password !== confirmPassword) {
      notifyError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `/api/auth/set-password`,
        {
          token,
          password,
        }
      );
      
      notifySuccess("Password set successfully!");
      setRegistrationStep('device');
    } catch (err) {
      notifyError(err.response?.data?.msg || "Error setting password.");
    } finally {
      setLoading(false);
    }
  };

  const requestDevice = async () => {
    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: ["ebed0e09-033a-4bfe-8dcc-20a04fad944e"] }],
      });

      setDevice(device);
      setDeviceInfo({ name: device.name || "Unknown" });
      notifyInfo("Device selected. Connecting...");
      await connectToDevice(device);
    } catch (error) {
      notifyError(`Failed to select a device: ${error.message}`);
    }
  };

  const connectToDevice = async (device) => {
    try {
      const server = await device.gatt.connect();
      notifySuccess("Connected to the device.");
      setDeviceConnected(true);
      await getDeviceServices(server);
    } catch (error) {
      notifyError(`Failed to connect: ${error.message}`);
    }
  };

  const getDeviceServices = async (server) => {
    try {
      const service = await server.getPrimaryService(
        "ebed0e09-033a-4bfe-8dcc-20a04fad944e"
      );
      const characteristics = await service.getCharacteristics();

      characteristics.forEach((char) => {
        if (char.properties.read) {
          readCharacteristicValue(char);
        }
      });
    } catch (error) {
      notifyError(
        `Failed to retrieve services or characteristics: ${error.message}`
      );
    }
  };

  const readCharacteristicValue = async (characteristic) => {
    try {
      const value = await characteristic.readValue();
      const valueArray = new Uint8Array(value.buffer);
      const base64Value = base64js.fromByteArray(valueArray);
      const decodedValue = atob(base64Value);

      if (characteristic.uuid === "c36e1c5a-fc6e-48c8-9a8e-d0b350399d0e") {
        setMacAddress(decodedValue);
      } else if (
        characteristic.uuid === "fbc47809-76ce-44fa-a2f0-676b95615473"
      ) {
        setUniqueId(decodedValue);
      }
    } catch (error) {
      notifyError(`Failed to read characteristic value: ${error.message}`);
    }
  };

  const handleCompleteRegistration = async () => {
    if (!skipDevice && (!macAddress || !uniqueId)) {
      notifyError("Please register the device first or choose to skip device registration.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `/api/auth/complete-registration`,
        {
          token,
          macAddress: skipDevice ? null : macAddress,
          uniqueId: skipDevice ? null : uniqueId,
          skipDevice,
        }
      );

      notifySuccess(res.data.message);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      notifyError(err.response?.data?.msg || "Error completing registration.");
    } finally {
      setLoading(false);
    }
  };

  const handleSkipDevice = () => {
    setSkipDevice(true);
    notifyInfo("Device registration skipped. You can use email OTP for login.");
  };

  if (!tokenValid) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying registration link...</p>
        </div>
        <ToastContainer />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 py-8">
      <div className="w-full max-w-md p-8 bg-white shadow-lg rounded-lg">
        <h2 className="text-2xl font-semibold mb-6 text-center">
          Complete Your Registration
        </h2>

        {userInfo && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Name:</strong> {userInfo.name}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Email:</strong> {userInfo.email}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Role:</strong> {userInfo.role}
            </p>
          </div>
        )}

        {registrationStep === 'password' && (
          <form onSubmit={handlePasswordSubmit}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Create Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength="6"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-blue-200"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength="6"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-blue-200"
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showConfirmPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-2 rounded-md transition ${
                loading
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
            >
              {loading ? "Setting Password..." : "Set Password"}
            </button>
          </form>
        )}

        {registrationStep === 'device' && (
          <div>
            <h3 className="text-lg font-semibold mb-4">
              Register Bluetooth Device (Optional)
            </h3>
            
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                You can register a Bluetooth device for secure login, or skip this step and use email OTP for authentication.
              </p>
            </div>

            {deviceConnected && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  ✓ Device "{deviceInfo.name}" connected successfully
                </p>
              </div>
            )}

            <div className="space-y-4">
              <button
                onClick={requestDevice}
                disabled={loading || deviceConnected}
                className={`w-full py-2 rounded-md transition ${
                  deviceConnected
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
              >
                {deviceConnected ? "Device Registered" : "Register Bluetooth Device"}
              </button>

              <button
                onClick={handleSkipDevice}
                disabled={loading}
                className="w-full py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition"
              >
                Skip Device Registration (Use Email OTP)
              </button>

              <button
                onClick={handleCompleteRegistration}
                disabled={loading || (!deviceConnected && !skipDevice)}
                className={`w-full py-2 rounded-md transition ${
                  loading || (!deviceConnected && !skipDevice)
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-green-500 text-white hover:bg-green-600"
                }`}
              >
                {loading ? "Completing Registration..." : "Complete Registration"}
              </button>
            </div>
          </div>
        )}

        <ToastContainer />
      </div>
    </div>
  );
};

export default CompleteRegistration;